import { useContext, useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { getZones, deleteZone, getCountries, exportZones, importZones } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import SearchInput from "../components/UI/SearchInput";
import SelectBox from "../components/UI/SelectBox";
import ConfirmModal from "../components/UI/ConfirmModal";
import ImportExportBar from "../components/UI/ImportExportBar";
import { notify } from "../utils/notify";
import DataTable from "../components/UI/DataTable";
import PageHeader from "../components/UI/PageHeader";
import { I18nContext, useTranslation } from "../context/I18nContext";

export default function Zones() {
  // Zone (and country) names can come back translated per the signed-in
  // user's own language (see ResolveEntityNames in
  // entity_translation_handler.go) — that resolution happens server-side at
  // fetch time, so this list needs to re-fetch whenever languageCode
  // changes, not just on mount — mirrors Categories.js's exact same pattern.
  const { languageCode } = useContext(I18nContext);
  const { t } = useTranslation("zones");
  const { t: tCommon } = useTranslation("common");
  const [zones, setZones] = useState([]);
  const [countries, setCountries] = useState([]);

  // countryFilter/searchTerm/currentPage are mirrored into the URL's query
  // string (via useSearchParams) instead of living only in local state.
  // Without this, navigating to /edit-zone/:id (a separate route) and back
  // unmounts this component and resets every filter to its default — this
  // was exactly the reported bug: filter by country, edit a zone, come
  // back, and the country filter is gone. Reading the initial value from
  // the URL here, combined with the `returnTo` state passed to
  // AddEditZone.js below (so it navigates back to THIS exact URL instead of
  // a bare "/zones"), is what makes the filter survive the round trip. It
  // also means a manual page refresh or the browser's back button restores
  // the same filtered view.
  const [searchParams, setSearchParams] = useSearchParams();
  const [countryFilter, setCountryFilter] = useState(searchParams.get("country") || "");
  const [searchTerm, setSearchTerm] = useState(searchParams.get("q") || "");
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get("page")) || 1);
  const [perPage] = useState(8);
  const [sortConfig, setSortConfig] = useState({ key: "id", direction: "asc" });
  const [modalConfig, setModalConfig] = useState({ isOpen: false, id: null });
  const [status, setStatus] = useState("idle");

  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem("token");

  // Keep the URL's query string in sync with the current filter/search/page
  // state, using `replace: true` so typing in the search box doesn't flood
  // the browser's back-button history with one entry per keystroke.
  useEffect(() => {
    const params = {};
    if (countryFilter) params.country = countryFilter;
    if (searchTerm) params.q = searchTerm;
    if (currentPage > 1) params.page = String(currentPage);
    setSearchParams(params, { replace: true });
  }, [countryFilter, searchTerm, currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchZones = async (countryId) => {
    try {
      const params = countryId ? { country_id: countryId } : undefined;
      const data = await getZones(token, params);
      setZones(data || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    getCountries(token).then(setCountries).catch(() => notify.error(t("notify_load_countries_failed", "Failed to load countries")));
  }, [languageCode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchZones(countryFilter); }, [countryFilter, languageCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const countryOptions = useMemo(
    () => [{ value: "", label: t("filter_country_all_option", "All countries") }, ...countries.map((c) => ({ value: String(c.id), label: c.name }))],
    [countries, t]
  );

  const sortedAndFiltered = useMemo(() => {
    let result = [...zones].filter((z) =>
      z.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      z.code?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    result.sort((a, b) => {
      let valA = a[sortConfig.key];
      let valB = b[sortConfig.key];
      if (typeof valA === "string") valA = valA.toLowerCase();
      if (typeof valB === "string") valB = valB.toLowerCase();

      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    return result;
  }, [zones, searchTerm, sortConfig]);

  const currentItems = sortedAndFiltered.slice((currentPage - 1) * perPage, currentPage * perPage);
  const totalPages = Math.ceil(sortedAndFiltered.length / perPage) || 1;

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
  };

  const handleDeleteClick = (id) => setModalConfig({ isOpen: true, id });

  const confirmDelete = async () => {
    try {
      setStatus("loading");
      await deleteZone(token, modalConfig.id);
      setStatus("success");
      setTimeout(() => {
        setModalConfig({ isOpen: false, id: null });
        setStatus("idle");
      }, 1200);
      notify.success(t("notify_deleted", "Zone deleted successfully"));
      fetchZones(countryFilter);
    } catch (err) {
      setStatus("error");
      notify.error(err.message || t("notify_delete_failed", "Failed to delete zone"));
    }
  };

  const columns = [
    { key: "id", label: tCommon("table_col_id", "ID"), className: "text-gray-400 font-mono" },
    { key: "name", label: t("col_name", "Zone Name"), className: "font-bold text-gray-700" },
    { key: "code", label: t("col_code", "Code"), className: "text-gray-600 font-mono" },
    {
      key: "country",
      label: t("col_country", "Country"),
      render: (row) => (
        <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-[10px] font-bold border border-gray-200">
          {row.country?.name || "—"}
        </span>
      ),
    },
  ];

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto transition-all duration-300">
      <ConfirmModal
        isOpen={modalConfig.isOpen}
        title={t("delete_confirm_title", "Delete Zone")}
        message={t("delete_confirm_message", "Are you sure? This may fail if cities are still linked to this zone.")}
        onConfirm={confirmDelete}
        onCancel={() => setModalConfig({ isOpen: false, id: null })}
        isLoading={status === "loading"}
        isSuccess={status === "success"}
        isError={status === "error"}
      />

      <PageHeader className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-6 border-b border-gray-100 pb-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/dashboard")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
            <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
          </button>
          <div className="flex items-center justify-center min-w-[48px] h-[48px] text-[var(--primary-color)]">
            <ICONS.Zones size={40} className="shrink-0" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold" style={{ color: "var(--primary-color)" }}>{t("page_title", "Zones")}</h2>
            <p className="text-gray-400 text-xs md:text-sm font-medium">{t("page_subtitle", "Manage regions/governorates within each country")}</p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
          <SearchInput
            value={searchTerm}
            onChange={(val) => { setSearchTerm(val); setCurrentPage(1); }}
            placeholder={t("search_placeholder", "Search zones...")}
          />
          <ImportExportBar
            entityLabel="Zones"
            fileBaseName="zones"
            onExport={() => exportZones(token)}
            onImport={(file) => importZones(token, file)}
            onImported={() => fetchZones(countryFilter)}
          />
          <Button variant="primary" icon={ICONS.Plus} onClick={() => navigate("/add-zone", { state: { returnTo: `${location.pathname}${location.search}` } })} className="w-full sm:w-auto shrink-0">
            {t("btn_add_zone", "Add Zone")}
          </Button>
        </div>
      </PageHeader>

      <div className="mb-6 max-w-xs">
        <SelectBox
          label={t("filter_country_label", "Filter by country")}
          options={countryOptions}
          value={countryFilter}
          onChange={(val) => { setCountryFilter(val); setCurrentPage(1); }}
        />
      </div>

      <DataTable
        columns={columns}
        data={currentItems}
        sortConfig={sortConfig}
        onSort={requestSort}
        renderActions={(row, isMobile) => (
          <>
            <Button variant="outline" size="sm" icon={ICONS.EditUser} onClick={() => navigate(`/edit-zone/${row.id}`, { state: { returnTo: `${location.pathname}${location.search}` } })}>{isMobile && ` ${tCommon("btn_edit", "Edit")}`}</Button>
            <Button variant="danger" size="sm" icon={ICONS.DeleteUser} onClick={() => handleDeleteClick(row.id)}>{isMobile && ` ${tCommon("btn_delete", "Delete")}`}</Button>
          </>
        )}
        mobileRender={(row) => (
          <div className="flex justify-between items-center">
            <span className="text-[10px] text-gray-400 font-mono">#{row.id} · {row.code}</span>
            <h3 className="font-bold text-gray-800">{row.name}</h3>
          </div>
        )}
        paginationConfig={{
          currentPage,
          totalPages,
          onPageChange: (p) => setCurrentPage(p),
          totalItems: sortedAndFiltered.length,
        }}
      />
    </div>
  );
}
